// « Apparence » — Auto · Clair · Sombre (owner spec 2026-10-02). A segmented
// control with a sliding gold thumb, 44 px tall. Lives in « More » (every
// user) and in Paramètres (admin); both read and write the one preference in
// lib/theme/adminTheme.ts, so they can never disagree. Since 2026-10-03 the
// choice is the signed-in person's (saved on the server, the editor follows).
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { useAdminTheme, type ThemePref } from '@/lib/theme/adminTheme';

const Svg = ({ children }: { children: ReactNode }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-[17px] h-[17px] shrink-0" aria-hidden>
    {children}
  </svg>
);
const ICON: Record<ThemePref, ReactNode> = {
  // half light, half dark: « like the phone »
  auto: <Svg><circle cx="12" cy="12" r="8.5" /><path d="M12 3.5a8.5 8.5 0 0 1 0 17z" fill="currentColor" stroke="none" /></Svg>,
  light: <Svg><circle cx="12" cy="12" r="4" /><path d="M12 2.5v2M12 19.5v2M4.6 4.6l1.4 1.4M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4L6 18M18 6l1.4-1.4" /></Svg>,
  dark: <Svg><path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" /></Svg>,
};
const OPTIONS: Array<{ value: ThemePref; label: string }> = [
  { value: 'auto', label: 'Auto' },
  { value: 'light', label: 'Clair' },
  { value: 'dark', label: 'Sombre' },
];

export function AppearanceSwitch({ className, showHint = true }: { className?: string; showHint?: boolean }) {
  const { t } = useT();
  const { pref, mode, staff, setPref } = useAdminTheme();
  const at = OPTIONS.findIndex((o) => o.value === pref);

  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = OPTIONS[(at + step + OPTIONS.length) % OPTIONS.length].value;
    setPref(next);
    const group = e.currentTarget.parentElement;
    requestAnimationFrame(() => group?.querySelector<HTMLButtonElement>(`[data-value="${next}"]`)?.focus());
  };

  return (
    <div className={className}>
      <div
        role="radiogroup"
        aria-label={t('Apparence')}
        data-appearance
        className="relative grid grid-cols-3 rounded-full p-1 select-none bg-adm-gold-wash"
        style={{ boxShadow: 'inset 0 0 0 1px var(--color-adm-hairline)' }}
      >
        <span
          aria-hidden
          className="absolute top-1 bottom-1 left-1 rounded-full bg-adm-gold transition-transform duration-200 ease-out motion-reduce:transition-none"
          style={{ width: 'calc((100% - 8px) / 3)', transform: `translateX(${at * 100}%)`, boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }}
        />
        {OPTIONS.map((o) => {
          const on = o.value === pref;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={on}
              data-value={o.value}
              tabIndex={on ? 0 : -1}
              onClick={() => setPref(o.value)}
              onKeyDown={onKey}
              className={cn(
                'relative z-10 h-11 rounded-full flex items-center justify-center gap-1.5 text-[14px] font-semibold transition-colors duration-150',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-adm-gold-ink/70',
                // on gold the ink is the fixed dark brown in both modes (6.5:1)
                on ? 'text-[#2E2418]' : 'text-adm-muted',
              )}
            >
              {ICON[o.value]}
              <span>{t(o.label)}</span>
            </button>
          );
        })}
      </div>
      {showHint && (
        <p className="mt-1.5 px-1 text-[12px] leading-snug text-adm-muted" aria-live="polite">
          {pref === 'auto'
            ? (mode === 'dark' ? t('Suit le réglage du téléphone — sombre en ce moment.') : t('Suit le réglage du téléphone — clair en ce moment.'))
            : staff ? t('Enregistré pour votre compte — partout, éditeur compris.') : t('Mémorisé sur cet appareil.')}
        </p>
      )}
    </div>
  );
}
